import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { newsApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import RichTextEditor from '../../components/ui/RichTextEditor'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import AdminImageField from '../../components/admin/AdminImageField'
import { buildImageFormData } from '../../utils/adminFormData'

const EMPTY = {
  slug: '',
  title_en: '',
  title_ar: '',
  excerpt_en: '',
  excerpt_ar: '',
  content_en: '',
  content_ar: '',
  category: '',
  image: '',
  published: true,
  is_published: true,
  featured: false,
}

function normalizeNews(item) {
  return {
    ...item,
    is_published: item.is_published ?? item.published ?? true,
    published: item.is_published ?? item.published ?? true,
  }
}

export default function AdminNews() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [saving, setSaving] = useState(false)

  const resetImageState = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
  }

  const handleImageSelect = (file) => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const load = () => {
    setLoading(true)
    newsApi
      .getAllAdmin({ per_page: 100 })
      .then(({ data }) => setItems(data.data || data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const base = {
        slug: form.slug,
        title_en: form.title_en,
        title_ar: form.title_ar,
        excerpt_en: form.excerpt_en,
        excerpt_ar: form.excerpt_ar,
        content_en: form.content_en,
        content_ar: form.content_ar,
        image: form.image || '',
        is_published: Boolean(form.is_published ?? form.published),
      }
      const payload = buildImageFormData(base, form, imageFile)
      if (form.id) await newsApi.update(form.id, payload)
      else await newsApi.create(payload)
      resetImageState()
      setForm(null)
      load()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.newsAdmin.confirmDelete'))) return
    await newsApi.delete(id)
    load()
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-primary">{t('admin.newsAdmin.title')}</h1>
        <Button onClick={() => { resetImageState(); setForm(EMPTY) }} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.newsAdmin.add')}
        </Button>
      </div>

      {form && (
        <Card className="mb-8">
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            {[
              ['slug', 'text', true],
              ['title_en', 'text'],
              ['title_ar', 'text'],
              ['category', 'text'],
              ['excerpt_en', 'textarea'],
              ['excerpt_ar', 'textarea'],
              ['content_en', 'richtext'],
              ['content_ar', 'richtext'],
            ].map(([field, type, required, labelKey]) => {
              const isArField = field.endsWith('_ar')
              const arFieldClass = isArField ? 'font-ar' : ''
              const arFieldDir = isArField ? { dir: 'rtl', lang: 'ar' } : {}
              const label = labelKey
                ? t(labelKey)
                : t(`admin.newsAdmin.${field}`) ||
                  t(`admin.pages.${field.replace('_en', 'En').replace('_ar', 'Ar')}`) ||
                  field
              const fieldId = `news-${field}`

              return type === 'richtext' ? (
                <AdminFormField key={field} label={label} htmlFor={fieldId} className="sm:col-span-2">
                  <RichTextEditor
                    value={form[field] || ''}
                    onChange={(html) => setForm({ ...form, [field]: html })}
                    dir={field.endsWith('_ar') ? 'rtl' : 'ltr'}
                  />
                </AdminFormField>
              ) : type === 'textarea' ? (
                <AdminFormField key={field} label={label} htmlFor={fieldId} className="sm:col-span-2">
                  <textarea
                    id={fieldId}
                    value={form[field]}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    rows={3}
                    {...arFieldDir}
                    className={`${adminInputClass} ${arFieldClass}`}
                  />
                </AdminFormField>
              ) : (
                <AdminFormField
                  key={field}
                  label={label}
                  htmlFor={fieldId}
                  className={field === 'slug' ? 'sm:col-span-2' : ''}
                >
                  <input
                    id={fieldId}
                    value={form[field]}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    required={required}
                    {...arFieldDir}
                    className={`${adminInputClass} ${arFieldClass}`}
                  />
                </AdminFormField>
              )
            })}
            <AdminImageField
              label={t('admin.newsAdmin.image')}
              htmlFor="news-image"
              className="sm:col-span-2"
              value={form.image}
              onChange={(image) => setForm({ ...form, image })}
              imageFile={imageFile}
              imagePreview={imagePreview}
              onFileSelect={handleImageSelect}
              disabled={saving}
            />
            <div className="flex gap-4 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_published ?? form.published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked, published: e.target.checked })}
                />
                {t('admin.pages.published')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                />
                {t('admin.newsAdmin.featured')}
              </label>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>
                {t('buttons.save')}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { resetImageState(); setForm(null) }}>
                {t('buttons.cancel')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-background">
              <tr>
                <th className="px-4 py-3 text-start">{t('admin.pages.titleEn')}</th>
                <th className="px-4 py-3 text-start">{t('admin.newsAdmin.category')}</th>
                <th className="px-4 py-3 text-end">{t('admin.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-border">
                  <td className="px-4 py-3">{item.title_en}</td>
                  <td className="px-4 py-3">{item.category}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => { resetImageState(); setForm(normalizeNews(item)) }} className="p-2 text-primary">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => handleDelete(item.id)} className="p-2 text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
